import { useEffect, useState } from "react";
import { Coffee, LogOut, Loader2, Pause } from "lucide-react";
import api, { errorMessage } from "../lib/api";
import { ActionButton, GhostButton } from "./Primitives";
import { toast } from "sonner";

/**
 * Compact banner that renders while the guard is on an active break.
 * Shows a live elapsed timer and an "End Break" button. When the break
 * exceeds the agency's paid-max cutoff, the badge flips to "unpaid" as a
 * heads-up (the server does the final classification at end-time).
 */
export function BreakBanner({ activeBreak, onEnded, paidMaxMinutes = 15 }) {
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(Date.now());

  useEffect(() => {
    const id = setInterval(() => setTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  if (!activeBreak) return null;

  const startMs = new Date(activeBreak.start_at).getTime();
  const elapsedSec = Math.max(0, Math.floor((tick - startMs) / 1000));
  const elapsedMin = Math.floor(elapsedSec / 60);
  const willBeUnpaid = elapsedMin >= paidMaxMinutes;

  const end = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/portal/break/${activeBreak.id}/end`);
      toast.success(
        r.data.break_type === "paid"
          ? `Break ended (${Math.round(r.data.duration_seconds / 60)}min — paid).`
          : `Break ended (${Math.round(r.data.duration_seconds / 60)}min — unpaid, deducted from timesheet).`
      );
      onEnded?.(r.data);
    } catch (e) {
      toast.error(errorMessage(e, "Failed to end break"));
    }
    setBusy(false);
  };

  return (
    <div
      data-testid="break-banner"
      className={`border p-4 ${willBeUnpaid ? "border-[#FF9F0A]/50 bg-[#FF9F0A]/5" : "border-[#0A84FF]/40 bg-[#0A84FF]/5"}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <Coffee className={`size-6 flex-shrink-0 ${willBeUnpaid ? "text-[#FF9F0A]" : "text-[#0A84FF]"}`} />
          <div className="min-w-0">
            <div className="label-mute flex items-center gap-2">
              <span className={willBeUnpaid ? "text-[#FF9F0A]" : "text-[#0A84FF]"}>ON BREAK</span>
              <span className="text-[10px] font-mono text-zinc-500">·</span>
              <span className="text-[10px] font-mono">
                <Pause className="size-3 inline mr-1" /> status checks paused
              </span>
            </div>
            <div className="font-display text-xl uppercase font-bold tracking-tight mt-0.5">
              <span data-testid="break-elapsed">
                {String(Math.floor(elapsedSec / 3600)).padStart(2, "0")}:
                {String(Math.floor((elapsedSec % 3600) / 60)).padStart(2, "0")}:
                {String(elapsedSec % 60).padStart(2, "0")}
              </span>
            </div>
            <div className={`text-[10px] uppercase tracking-widest font-mono mt-1 ${willBeUnpaid ? "text-[#FF9F0A]" : "text-zinc-500"}`}>
              {paidMaxMinutes <= 0
                ? "unpaid site — full duration deducted"
                : willBeUnpaid
                  ? `over ${paidMaxMinutes}min — will be unpaid`
                  : `paid up to ${paidMaxMinutes}min`}
            </div>
          </div>
        </div>
        <ActionButton data-testid="end-break-button" disabled={busy} onClick={end}>
          {busy ? <Loader2 className="size-4 animate-spin" /> : <LogOut className="size-4" />} End Break
        </ActionButton>
      </div>
    </div>
  );
}

export function StartBreakButton({ disabled, onStarted }) {
  const [busy, setBusy] = useState(false);
  const start = async () => {
    setBusy(true);
    try {
      const r = await api.post("/portal/break/start", {});
      toast.success("Break started. Status checks paused.");
      onStarted?.(r.data);
    } catch (e) {
      toast.error(errorMessage(e, "Failed to start break"));
    }
    setBusy(false);
  };
  return (
    <GhostButton data-testid="start-break-button" disabled={disabled || busy} onClick={start}>
      {busy ? <Loader2 className="size-4 animate-spin" /> : <Coffee className="size-4" />} Start Break
    </GhostButton>
  );
}
